/**
 * @fileoverview QuestionPreview Component
 *
 * Preview component for displaying questions as they would appear to students.
 * Renders different UI elements based on question type and displays materials,
 * context, and expected answers in a formatted way.
 *
 * Features:
 * - Type-specific rendering (multiple choice, true/false, text input)
 * - Material display (images, audio with players)
 * - Context integration with transcripts
 * - Expected answer highlighting
 * - Exam type badges with color coding
 *
 * @module QuestionPreview
 */

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import {
  FileText,
  Mic,
  BookOpen,
  Clock,
  Lightbulb,
  Edit,
  Loader2,
  Eye,
} from "lucide-react";
import { getQuestionsByContextId } from "@/lib/questionApi";
import { fetchContextBinaryContent } from "@/lib/contextApi";
import { LazyBinaryContent } from "@/components/admin/answersManager/LazyBinaryContent";
import { logger } from '@/lib/logger';
import { isOptionExpected, optionText, type OptionValue } from '@/lib/questionOptions';
import OptionTagBadges from '@/components/admin/OptionTagBadges';
import { type QuestionTypeValue } from '@/components/admin/questionForm/constants';

/**
 * Interface for context objects attached to questions
 *
 * @interface Context
 * @property {number} id - Unique context identifier
 * @property {string} name - Context name/title
 * @property {'text' | 'image' | 'audio'} type - Type of context material
 * @property {string} content - Text content or binary data (base64)
 * @property {string} [fileExtension] - File extension for binary content
 * @property {string} [mimeType] - MIME type for binary content
 * @property {string} [binaryContent] - Base64-encoded binary content
 * @property {string | null} [mediaUrl] - URL to stored media asset
 * @property {string} [transcript] - Audio transcript for audio contexts
 */
interface Context {
  id: number;
  name: string;
  type: "text" | "image" | "audio";
  content: string;
  fileExtension?: string;
  mimeType?: string;
  binaryContent?: string;
  mediaUrl?: string | null;
  transcript?: string;
  generationModel?: string | null;
}

/**
 * Interface for question data structure
 *
 * @interface Question
 * @property {string} [id] - Unique question identifier
 * @property {string} text - Question text/prompt
 * @property {QuestionTypeValue} type - Question type
 * @property {string} skill - Associated skill/competence
 * @property {string} [topic] - Optional topic category
 * @property {'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2'} level - CEFR proficiency level
 * @property {string[]} [options] - Array of answer options for multiple choice questions
 * @property {string | string[]} expectedAnswer - Expected answer(s)
 * @property {string} explanation - Explanation of expected answer
 * @property {string} [materialUrl] - URL to associated material
 * @property {'image' | 'audio' | 'video'} [materialType] - Type of associated material
 * @property {number} section - Section number in exam
 * @property {string[]} examTypes - Array of exam types this question belongs to
 * @property {Context} [context] - Optional context object
 * @property {number} [preparationTime] - Preparation time in seconds for audio-recording questions (0-60)
 * @property {number} [recordingDuration] - Recording duration in seconds for audio-recording questions (1-600)
 */
interface Question {
  id?: string;
  text: string;
  type: QuestionTypeValue;
  skill: string;
  topic?: string;
  level: "A1" | "A2" | "B1" | "B2" | "C1" | "C2";
  options?: Array<string | OptionValue>;
  expectedAnswer: string | string[];
  explanation: string;
  materialUrl?: string;
  materialType?: "image" | "audio" | "video";
  section: number;
  examTypes: string[];
  preparationTime?: number;
  recordingDuration?: number;
  context?: Context;
  generationModel?: string | null;
}

/**
 * Props interface for QuestionPreview component
 *
 * @interface QuestionPreviewProps
 * @property {Question} question - Question object to preview
 * @property {() => void} [onEdit] - Optional callback when edit button is clicked
 * @property {() => void} [onClose] - Optional callback when close button is clicked
 * @property {(question: Question) => void} [onViewRelated] - Optional callback when viewing a related question
 */
interface QuestionPreviewProps {
  readonly question: Question;
  readonly onEdit?: () => void;
  readonly onClose?: () => void;
  readonly onViewRelated?: (question: Question) => void;
}

/**
 * QuestionPreview Component
 *
 * Renders a formatted preview of a question showing how it will appear
 * to students during an exam. Displays question content, materials, context,
 * and highlights expected answers.
 *
 * @param {QuestionPreviewProps} props - Component props
 * @returns {JSX.Element} Formatted question preview
 *
 * @component
 * @example
 * ```tsx
 * <QuestionPreview question={questionData} />
 * ```
 */
export default function QuestionPreview({
  question,
  onEdit,
  onClose,
  onViewRelated,
}: QuestionPreviewProps) {
  const [relatedQuestions, setRelatedQuestions] = useState<Question[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  /**
   * Fetch related questions for the context.
   */
  const fetchRelatedQuestions = async (contextId: number | string, currentQuestionId?: string) => {
    setLoadingRelated(true);
    try {
      const contextIdStr = String(contextId);
      const questions = await getQuestionsByContextId(contextIdStr, currentQuestionId);
      setRelatedQuestions(questions);
    } catch (error) {
      logger.error("Error fetching related questions:", error);
      setRelatedQuestions([]);
    } finally {
      setLoadingRelated(false);
    }
  };

  // Fetch related questions when component mounts if context exists
  useEffect(() => {
    if (!question.context?.id) {
      return;
    }
    fetchRelatedQuestions(question.context.id, question.id);
  }, [question.context?.id, question.id]);

  /**
   * Render audio recording time information.
   */
  const renderAudioTimeInfo = () => {
    if (question.type !== "audio-recording" && question.type !== "live-conversation") {
      return null;
    }
    if (!question.preparationTime && !question.recordingDuration) {
      return null;
    }

    return (
      <>
        <span>•</span>
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {question.preparationTime > 0 && (
            <span>Prep: {question.preparationTime}s</span>
          )}
          {!!(question.preparationTime > 0 && question.recordingDuration > 0) && (
            <span>|</span>
          )}
          {question.recordingDuration > 0 && (
            <span>Record: {question.recordingDuration}s</span>
          )}
        </div>
      </>
    );
  };

  /**
   * Render context content based on type.
   */
  const renderContextContent = () => {
    if (!question.context) {
      return null;
    }

    if (question.context.type === "text") {
      return (
        <div className="prose max-w-none max-h-48 overflow-y-auto text-sm">
          <p className="font-ui whitespace-pre-wrap">
            {question.context.content}
          </p>
        </div>
      );
    }

    if (question.context.type === "image" || question.context.type === "audio") {
      return (
        <div>
          {!!question.context.id && (
            <LazyBinaryContent
              contextId={question.context.id}
              mimeType={question.context.mimeType}
              contextTitle={question.context.name}
              contextType={question.context.type}
              mediaUrl={question.context.mediaUrl}
              initialBase64={question.context.binaryContent}
              fetchBinaryContent={fetchContextBinaryContent}
            />
          )}

          {question.context.type === "audio" && question.context.transcript && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-primary" />
                <span className="font-ui font-medium text-sm">
                  Transcript:
                </span>
              </div>
              <div className="border rounded-lg p-3 bg-muted">
                <p className="font-ui text-sm whitespace-pre-wrap">
                  {question.context.transcript}
                </p>
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  /**
   * Get related questions description text.
   */
  const getRelatedQuestionsDescription = (): string => {
    if (loadingRelated) {
      return 'Loading...';
    }
    if (relatedQuestions.length === 0) {
      return 'No other questions use this context';
    }
    const questionText = relatedQuestions.length === 1 ? 'question' : 'questions';
    return `${relatedQuestions.length} other ${questionText} use this context`;
  };

  /**
   * Render related questions content based on loading and data state.
   */
  const renderRelatedQuestionsContent = () => {
    if (loadingRelated) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        </div>
      );
    }

    if (relatedQuestions.length === 0) {
      return (
        <p className="font-ui text-xs text-muted-foreground text-center py-2">
          No other questions use this context
        </p>
      );
    }

    return (
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {relatedQuestions.map((relatedQ) => (
          <div
            key={relatedQ.id}
            className="p-2 bg-background rounded border border-border hover:border-primary/30 transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-ui text-xs text-foreground flex-1 line-clamp-2">
                {relatedQ.text}
              </p>
              {onViewRelated && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 flex-shrink-0 hover:bg-primary/10"
                  onClick={() => onViewRelated(relatedQ)}
                  title="View question preview"
                >
                  <Eye className="h-4 w-4 text-primary" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  /**
   * Get icon component based on question type
   *
   * Returns appropriate icon for visual identification of question type.
   * Differentiates between short-form questions (FileText) and long-form
   * questions (BookOpen).
   *
   * @function getTypeIcon
   * @param {string} type - Question type string
   * @returns {JSX.Element} Icon component
   */
  const getTypeIcon = (type: string) => {
    switch (type) {
      case "multiple-choice-single":
      case "multiple-choice-multi":
      case "true-false":
      case "text-short":
      case "fill-in-blank":
        return <FileText className="h-5 w-5" />;
      case "text-long":
        return <BookOpen className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  /**
   * Get Tailwind CSS classes for exam type badge styling
   *
   * Returns color-coded CSS classes based on exam type for visual
   * differentiation in the UI. Each exam type has distinct color scheme.
   *
   * @function getExamTypeColor
   * @param {string} examType - Exam type string (goethe/ösd/telc)
   * @returns {string} Tailwind CSS classes for badge styling
   *
   * Color Mapping:
   * - Goethe: Blue theme (bg-blue-500/10 text-blue-600 border-blue-200)
   * - ÖSD: Green theme (bg-green-500/10 text-green-600 border-green-200)
   * - telc: Purple theme (bg-purple-500/10 text-purple-600 border-purple-200)
   * - Default: Gray theme for unknown types
   */
  const getExamTypeColor = (examType: string) => {
    switch (examType.toLowerCase()) {
      case "goethe":
        return "bg-primary/10 text-primary border-primary/20";
      case "ösd":
        return "bg-primary/10 text-primary border-primary/20";
      case "telc":
        return "bg-primary/10 text-primary border-primary/20";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  /**
   * Render question content based on question type
   *
   * Dynamically renders appropriate UI elements based on question type.
   * Highlights expected answers in green and handles single/multiple
   * answer scenarios.
   *
   * @function renderQuestionContent
   * @returns {JSX.Element} Question-specific UI rendering
   *
   * Question Types Handled:
   * - multiple-choice-single: Radio buttons with single expected answer
   * - multiple-choice-multi: Checkboxes with multiple expected answers
   * - true-false: Boolean choice with True/False options
   * - text-short: Short text input field
   * - text-long: Long text area
   */
  const renderQuestionContent = () => {
    switch (question.type) {
      case "multiple-choice-single":
        return (
          <div className="space-y-3">
            <h3 className="font-ui text-base font-semibold">{question.text}</h3>
            <RadioGroup className="space-y-2">
              {question.options?.map((rawOption, index) => {
                const label = optionText(rawOption as OptionValue);
                const isCorrect = isOptionExpected(
                  rawOption,
                  index,
                  question.expectedAnswer,
                );
                return (
                <div key={`option-${index}-${label}`} className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={label}
                    id={`option-${index}`}
                    disabled
                  />
                  <Label
                    htmlFor={`option-${index}`}
                    className={`flex flex-1 flex-wrap items-center gap-2 cursor-pointer ${
                      isCorrect
                        ? "font-semibold text-primary"
                        : ""
                    }`}
                  >
                    <span>{label}</span>
                    <OptionTagBadges option={rawOption as OptionValue} />
                    {isCorrect && (
                      <span className="text-primary">✓</span>
                    )}
                  </Label>
                </div>
              );
              })}
            </RadioGroup>
          </div>
        );

      case "multiple-choice-multi":
        return (
          <div className="space-y-3">
            <h3 className="font-ui text-base font-semibold">{question.text}</h3>
            <div className="space-y-2">
              {question.options?.map((rawOption, index) => {
                const label = optionText(rawOption as OptionValue);
                const isCorrect = isOptionExpected(
                  rawOption,
                  index,
                  question.expectedAnswer,
                );
                return (
                  <div key={`option-${index}-${label}`} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={isCorrect}
                      disabled
                      className="h-4 w-4"
                    />
                    <Label
                      className={`flex flex-1 flex-wrap items-center gap-2 cursor-pointer ${
                        isCorrect ? "font-semibold text-green-600" : ""
                      }`}
                    >
                      <span>{label}</span>
                      <OptionTagBadges option={rawOption as OptionValue} />
                      {isCorrect && (
                        <span className="text-green-600">✓</span>
                      )}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "true-false":
        return (
          <div className="space-y-3">
            <h3 className="font-ui text-base font-semibold">{question.text}</h3>
            <RadioGroup className="space-y-2">
              {["True", "False"].map((option) => {
                // Normalize comparison to handle both "True"/"true" and "False"/"false" from database
                const isCorrect =
                  option.toLowerCase() ===
                  String(question.expectedAnswer || "").toLowerCase();
                // Find the stored option object (synthesized stance) for tag display
                const storedOption = question.options?.find(
                  (o) => typeof o === "object" && o !== null &&
                    "key" in o &&
                    String((o as OptionValue).key).toLowerCase() === option.toLowerCase()
                ) as OptionValue | undefined;
                return (
                  <div key={option} className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={option}
                      id={`tf-${option}`}
                      disabled
                    />
                    <Label
                      htmlFor={`tf-${option}`}
                      className={`flex flex-1 flex-wrap items-center gap-2 cursor-pointer ${
                        isCorrect ? "font-semibold text-green-600" : ""
                      }`}
                    >
                      <span>{option}</span>
                      {storedOption && <OptionTagBadges option={storedOption} />}
                      {isCorrect && (
                        <span className="text-green-600">✓</span>
                      )}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        );

      case "text-short":
        return (
          <div className="space-y-3">
            <h3 className="font-ui text-base font-semibold">{question.text}</h3>
            <div className="space-y-2">
              <Label className="font-ui text-sm font-medium">
                Answer:
              </Label>
              <Input
                value={question.expectedAnswer as string}
                disabled
                className="bg-primary/10 border-primary/20"
              />
            </div>
          </div>
        );

      case "text-long":
        return (
          <div className="space-y-3">
            <h3 className="font-ui text-base font-semibold">{question.text}</h3>
            <div className="space-y-2">
              <Label className="font-ui text-sm font-medium">
                Sample Answer:
              </Label>
              <Textarea
                value={question.expectedAnswer as string}
                disabled
                className="bg-primary/10 border-primary/20 min-h-[100px]"
              />
            </div>
          </div>
        );

      case "fill-in-blank": {
        // Parse the question text to find blanks marked with ___
        const parts = question.text.split('___');
        let expectedAnswers: string[];
        if (Array.isArray(question.expectedAnswer)) {
          expectedAnswers = question.expectedAnswer;
        } else if (typeof question.expectedAnswer === 'string') {
          try {
            expectedAnswers = JSON.parse(question.expectedAnswer || '[]');
          } catch {
            expectedAnswers = [];
          }
        } else {
          expectedAnswers = [];
        }
        
        return (
          <div className="space-y-3">
            <div className="font-ui text-base font-semibold whitespace-pre-line">
              {parts.map((part, index) => {
                const partKey = `part-${index}-${part.substring(0, 10).replaceAll(' ', '-')}`;
                if (index === parts.length - 1) {
                  return <span key={partKey}>{part}</span>;
                }
                const answer = expectedAnswers[index] || '';
                return (
                  <span key={partKey} className="inline-flex items-center gap-1">
                    <span>{part}</span>
                    <Input
                      value={answer}
                      disabled
                      className="inline-block w-32 bg-primary/10 border-primary/20 text-primary font-semibold"
                      placeholder="___"
                    />
                  </span>
                );
              })}
            </div>
            {expectedAnswers.length > 0 && (
              <div className="space-y-2 mt-4">
                <Label className="font-ui text-sm font-medium">
                  Expected Answers:
                </Label>
                <div className="flex flex-wrap gap-2">
                  {expectedAnswers.map((answer: string, idx: number) => (
                    <Badge key={`answer-${idx}-${answer.substring(0, 10)}`} variant="outline" className="bg-primary/10 border-primary/20">
                      {idx + 1}: {answer}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }

      case "audio-recording":
        return (
          <div className="space-y-3">
            <h3 className="font-ui text-base font-semibold">{question.text}</h3>
            <div className="p-3 bg-muted border border-border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Mic className="h-4 w-4 text-primary" />
                <span className="font-ui text-sm font-medium text-foreground">
                  Audio Recording Question
                </span>
              </div>
              <div className="font-ui text-xs text-muted-foreground space-y-1">
                {question.preparationTime !== undefined &&
                  question.preparationTime > 0 && (
                    <p>
                      Preparation time: {question.preparationTime} seconds
                    </p>
                  )}
                {question.recordingDuration !== undefined &&
                  question.recordingDuration > 0 && (
                    <p>
                      Recording duration: {question.recordingDuration} seconds
                    </p>
                  )}
              </div>
            </div>
            {question.expectedAnswer && (
              <div className="space-y-1">
                <Label className="font-ui text-xs font-medium text-muted-foreground">
                  Expected Answer Format:
                </Label>
                <p className="font-ui text-sm text-muted-foreground italic">
                  {question.expectedAnswer}
                </p>
              </div>
            )}
          </div>
        );

      case "live-conversation":
        return (
          <div className="space-y-3">
            <h3 className="font-ui text-base font-semibold">{question.text}</h3>
            <div className="p-3 bg-muted border border-border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Mic className="h-4 w-4 text-primary" />
                <span className="font-ui text-sm font-medium text-foreground">
                  Live Conversation Question (AI-led)
                </span>
              </div>
              <div className="font-ui text-xs text-muted-foreground space-y-1">
                {question.preparationTime !== undefined &&
                  question.preparationTime > 0 && (
                    <p>
                      Thinking time: {question.preparationTime} seconds
                    </p>
                  )}
                <p>Recording handled by the Realtime bridge (no manual duration needed).</p>
              </div>
            </div>
            {question.expectedAnswer && (
              <div className="space-y-1">
                <Label className="font-ui text-xs font-medium text-muted-foreground">
                  Response Criteria:
                </Label>
                <p className="font-ui text-sm text-muted-foreground italic">
                  {question.expectedAnswer}
                </p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="space-y-4">
            <h3 className="font-ui text-lg font-semibold">{question.text}</h3>
          </div>
        );
    }
  };

  return (
    <div className="space-y-3 min-h-0">
      {/* Question Header - Compact */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="bg-primary/10 p-1.5 rounded">
            {getTypeIcon(question.type)}
          </div>
          <div>
            <h2 className="font-ui text-lg font-bold">
              Question Preview
            </h2>
            <div className="font-ui flex items-center gap-2 text-xs text-muted-foreground">
              <span>{question.skill}</span>
              <span>•</span>
              <span>
                Section {question.section}
              </span>
              {renderAudioTimeInfo()}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="secondary" className="font-ui text-xs">
            {question.level}
          </Badge>
          <Badge variant="outline" className="font-ui text-xs">
            Sec {question.section}
          </Badge>
          {question.examTypes && question.examTypes.length > 0 && (
            <Badge
              className={`font-ui text-xs ${getExamTypeColor(question.examTypes[0])}`}
            >
              {question.examTypes[0].toUpperCase()}
            </Badge>
          )}
          <Badge variant="outline" className="font-ui text-xs capitalize">
            {question.type.replace("-", " ")}
          </Badge>
          {question.generationModel && (
            <Badge variant="outline" className="font-ui text-xs">
              Model: {question.generationModel}
            </Badge>
          )}
        </div>
      </div>

      {/* Context Section - Compact */}
      {question.context && (
        <Card className="bg-primary/5 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="font-ui text-sm font-semibold flex flex-wrap items-center gap-1.5">
              <FileText className="h-4 w-4" />
              <span>Context: {question.context.name}</span>
              {question.context.generationModel && (
                <Badge variant="outline" className="font-ui text-xs">
                  Model: {question.context.generationModel}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {renderContextContent()}
          </CardContent>
        </Card>
      )}

      {/* Question Content and Explanation - Combined Compact */}
      <Card>
        <CardContent className="p-3">
          <div className="space-y-3">
            <div className="max-h-64 overflow-y-auto">
              {renderQuestionContent()}
            </div>

            {/* Explanation - Inline */}
            <div className="pt-2 border-t">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <span className="font-ui text-xs font-medium text-foreground">
                    Explanation:
                  </span>
                  <p className="font-ui text-xs text-muted-foreground mt-1 whitespace-pre-wrap max-h-32 overflow-y-auto">
                    {question.explanation}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Related Questions Section - Only show if context exists */}
      {question.context && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="font-ui text-sm font-semibold flex items-center gap-1.5">
              <FileText className="h-4 w-4" />
              Other Questions with Same Context
            </CardTitle>
            <CardDescription className="font-ui text-xs">
              {getRelatedQuestionsDescription()}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {renderRelatedQuestionsContent()}
          </CardContent>
        </Card>
      )}

      {/* Footer with Close and Edit buttons */}
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        {onEdit && (
          <Button onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        )}
      </div>
    </div>
  );
}
